import { navigationMenus } from 'app/config'

/**
 * The permission catalogue == the Alfred tab tree.
 *
 * The sidebar half is derived from `navigationMenus` so it can never drift out
 * of sync with the actual IA. The in-page tab strips (Procurement HQ's
 * Dashboard/Budgets/…, Users' Student/Guardians/…) are NOT in `navigationMenus`
 * — each page owns a local TABS array — so they are declared below, keyed by the
 * `name` of the menu item they hang off. A tab may itself carry `tabs` for the
 * one case that goes three deep (Programs -> Subjects -> Regular/Playdate/…).
 *
 * Routes with `sideBarVisibility: false` are skipped: they are the URL aliases
 * of the in-page tabs (`/people/parent` IS the Users -> Guardians tab), so
 * including them would duplicate every tab.
 */
const IN_PAGE_TABS = {
  search: ['Parent', 'Teacher'],
  centerTeamProcurement: [
    'Dashboard',
    'Program Material',
    'Consumables',
    'Petty Cash',
    'Repair & Maintenance',
    'Notifications',
    'Catalogue'
  ],
  hqProcurement: [
    'Monthly Dashboard',
    'Monthly Budgets',
    'Monthly Orders',
    'Monthly Consumables',
    'Daily Sheet',
    'Catalogue',
    'SOP'
  ],
  batch: ['Common', 'Summer Camp'],
  programs: [
    'Categories',
    { label: 'Subjects', tabs: ['Regular', 'Playdate', 'Toddler Club'] },
    'Standards',
    'Courses'
  ],
  users: ['Student', 'Guardians', 'Teacher', 'Admin'],
  enrolments: [
    'Event',
    'Excel',
    'Explore',
    'School & Care',
    'Toddler Club',
    'Trial & Drop-In',
    'Make-Up'
  ],
  orders: ['Regular', 'Instalment', 'Drop-In Care'],
  apartmentManage: [
    'Overview',
    'Parent Dashboard',
    'Booking',
    'Batches',
    'Attendance',
    'Trials',
    'Waitlist',
    'Payments',
    'Pricing',
    'Payout'
  ],
  apartmentProcurement: [
    'Overview',
    'Requests',
    'Approvals',
    'Budgets',
    'Catalogue'
  ],
  // ACADS' menu entry is parked (2026-07, "one-step re-enable"), so this key
  // currently matches nothing. Left in place so the tabs come back with it.
  acadsHome: ['Reconciliation', 'Receipts', 'Students', 'Teacher Payouts']
}

const slugify = (label) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

/**
 * The permission id of a menu item, given its parent's id. THE one place ids are
 * built.
 *
 * The catalogue (this file), the sidebar prune (MainLayout) and the route guard
 * (RouteHandler/PrivateRoute) must all agree on the id, or a role will grant a
 * tab the sidebar then hides — a bug that looks like a permissions bug and is
 * really a string-formatting bug. Everything derives ids from here; nobody
 * rebuilds them by hand.
 */
export const idFor = (item, parentId) =>
  parentId ? `${parentId}.${item.name}` : item.name

/** Same, for an in-page tab (which has a label, not a menu `name`). */
export const tabIdFor = (label, parentId) => `${parentId}.${slugify(label)}`

const toTabNodes = (tabs, parentId) =>
  (tabs || []).map((tab) => {
    const { label, tabs: nested } =
      typeof tab === 'string' ? { label: tab } : tab
    const id = tabIdFor(label, parentId)
    return { id, label, kind: 'tab', children: toTabNodes(nested, id) }
  })

// menu item `name` -> its full dotted id. Lets a page ask for its own tab ids
// without knowing where it sits in the tree (see tabIdForMenu).
const menuIdByName = {}

const toMenuNode = (item, parentId) => {
  const id = idFor(item, parentId)
  menuIdByName[item.name] = id
  const visibleSubMenus = (item.subMenus || []).filter(
    (sub) => sub.sideBarVisibility !== false
  )
  return {
    id,
    label: item.displayName || item.name,
    kind: parentId ? 'page' : 'section',
    children: [
      ...visibleSubMenus.map((sub) => toMenuNode(sub, id)),
      ...toTabNodes(IN_PAGE_TABS[item.name], id)
    ]
  }
}

export const permissionTree = navigationMenus.map((group) =>
  toMenuNode(group, '')
)

const walk = (nodes, visit) =>
  nodes.forEach((node) => {
    visit(node)
    walk(node.children, visit)
  })

// id -> node. Built once at module load; the tree is static.
const nodeById = {}
walk(permissionTree, (node) => {
  nodeById[node.id] = node
})

export const getNode = (id) => nodeById[id]

export const allPermissionIds = Object.keys(nodeById)

export const totalPermissions = allPermissionIds.length

export const descendantIds = (node) => {
  const ids = []
  walk(node.children, (child) => ids.push(child.id))
  return ids
}

/**
 * A parent is selected iff every child is. Callers only ever set/unset a node
 * and its subtree; this re-derives the ancestors so the tri-state checkboxes
 * and the stored selection agree.
 */
const syncAncestors = (selected) => {
  const byDepth = [...allPermissionIds].sort(
    (a, b) => b.split('.').length - a.split('.').length
  )
  byDepth.forEach((id) => {
    const node = nodeById[id]
    if (!node.children.length) return
    const allChildrenOn = node.children.every((child) => selected.has(child.id))
    if (allChildrenOn) selected.add(id)
    else selected.delete(id)
  })
  return selected
}

/**
 * The id of an in-page tab, looked up from the menu item it hangs off. A page
 * knows its own menu `name` but not its position in the tree, so this resolves
 * the parent id for it. Returns null for a menu name that isn't in the nav
 * (e.g. ACADS, currently parked) — callers treat that as "not gateable".
 */
export const tabIdForMenu = (menuName, label) => {
  const parentId = menuIdByName[menuName]
  return parentId ? tabIdFor(label, parentId) : null
}

/**
 * THE access rule. Every gate (sidebar, route, tab) goes through this.
 *
 * `access` is the `alfred_access` object from GET /base/me:
 *   { role, unrestricted, permissions[] }
 *
 * `unrestricted` is true both when the user has no role (the norm — nobody has
 * one until it is assigned, and per product they see everything) and when their
 * role is Super Admin (which must keep seeing sections added later). The backend
 * collapses both into that one flag so there is exactly one rule here.
 *
 * NOTE: this hides UI. It is NOT authorization — the APIs behind a hidden
 * section remain callable. Deliberate; see the Role docstring in oh-api.
 */
// Offline-demo grants: with the procurement mock on (dev server only — named
// builds never load .env.development.local), the At Apartment procurement
// surfaces are treated as granted. The real grant is user migration 0051;
// until it runs on the backend this session talks to, the stored role list
// cannot contain these ids and the sidebar would hide the very pages the
// mock exists to demo.
const MOCK_GRANTED_IDS =
  process.env.REACT_APP_PROCUREMENT_MOCK === 'true'
    ? [
        'apartments.apartmentProcurement',
        'apartments.apartmentProcurement.overview',
        'apartments.apartmentProcurement.requests',
        'apartments.apartmentProcurement.approvals',
        'apartments.apartmentProcurement.budgets',
        'apartments.apartmentProcurement.catalogue',
        'hq.hqProcurement.at-apartment'
      ]
    : []

export const hasPermission = (access, id) => {
  if (!access) return false // not loaded yet — callers should render a spinner
  if (access.unrestricted) return true
  if (MOCK_GRANTED_IDS.includes(id)) return true
  return (access.permissions || []).includes(id)
}

/**
 * Can this user reach the thing at `id` at all — the question the sidebar and
 * the router ask (as opposed to hasPermission, which is the exact-tick question
 * the checkbox tree asks).
 *
 * The difference matters. A parent id is only stored in a role when EVERY child
 * was ticked (that is how the tri-state tree normalises). So a Centre Manager
 * granted just the Student and Guardians tabs does NOT hold `usergroup.users` —
 * and gating the Users page on an exact match would hide the very page those
 * tabs live on. Reaching a page therefore means: hold it, or hold anything under
 * it.
 *
 * Ids absent from the catalogue fail OPEN. Those are the hidden tab-alias routes
 * (/people/parent IS the Users -> Guardians tab), which have no catalogue entry
 * of their own; blocking them would 404 a tab the user is allowed to see. The
 * tab strip still hides the tab itself, and since none of this is an
 * authorization boundary anyway, an unrecognised id is not worth a lockout.
 */
export const canAccess = (access, id) => {
  if (!access) return false
  if (access.unrestricted) return true

  const node = nodeById[id]
  if (!node) return true

  if (hasPermission(access, id)) return true
  return descendantIds(node).some((each) => hasPermission(access, each))
}

/**
 * Prune a navigationMenus tree down to what `access` may see.
 *
 * Done as a pre-pass over the tree rather than as a check inside the sidebar's
 * render, so MainLayout's auto-collapse logic (a group with exactly one visible
 * child renders as a direct link) keeps working on the pruned shape for free.
 *
 * A group whose children all get pruned is dropped too — otherwise the sidebar
 * shows an empty expander that opens onto nothing.
 *
 * Hidden-but-routable items (sideBarVisibility: false) are KEPT: they are the
 * URL aliases of in-page tabs, and dropping them here would also delete their
 * routes (RouteHandler builds routes from this same tree), 404-ing a tab the
 * user is allowed to see.
 */
export const pruneMenusByAccess = (menus, access, parentId = '') => {
  if (!access || access.unrestricted) return menus

  return menus
    .map((item) => {
      const id = idFor(item, parentId)

      if (item.subMenus) {
        const subMenus = pruneMenusByAccess(item.subMenus, access, id)
        const hasVisibleChild = subMenus.some(
          (sub) => sub.sideBarVisibility !== false
        )
        return hasVisibleChild ? { ...item, subMenus } : null
      }

      // A routable leaf that is not in the sidebar is a tab alias — keep the
      // route, let the tab strip decide whether to show the tab.
      if (item.sideBarVisibility === false) return item

      return canAccess(access, id) ? item : null
    })
    .filter(Boolean)
}

/**
 * Is this user a Super Admin?
 *
 * Specifically the ROLE, not `unrestricted`. Those are not the same thing:
 * `unrestricted` is also true for a user with NO role at all, and it is what
 * the reducer falls back to when /base/me fails (it fails OPEN so a flaky
 * request can't lock admins out of the portal). Gating on `unrestricted` would
 * therefore show a Super-Admin-only item to the LEAST privileged accounts and
 * to everyone during an outage — the exact opposite of the intent.
 */
export const isSuperAdmin = (access) => Boolean(access?.role?.is_super_admin)

/**
 * Prune menu items marked `superAdminOnly`.
 *
 * A second pass, separate from pruneMenusByAccess, because that one returns
 * early for unrestricted roles — which is exactly the group these items need
 * hiding from. Anything without `superAdminOnly` is untouched.
 *
 * This is presentation only. The endpoints behind such a page are NOT
 * restricted, and the route stays reachable if someone types the URL. Use it
 * for reducing clutter or staging a rollout, never as a data-access control —
 * for that, add a real permission id and grant it.
 */
export const pruneMenusBySuperAdmin = (menus, access) => {
  const superAdmin = isSuperAdmin(access)

  return menus
    .map((item) => {
      if (item.superAdminOnly && !superAdmin) return null

      if (item.subMenus) {
        const subMenus = pruneMenusBySuperAdmin(item.subMenus, access)
        const hasVisibleChild = subMenus.some(
          (sub) => sub.sideBarVisibility !== false
        )
        return hasVisibleChild ? { ...item, subMenus } : null
      }

      return item
    })
    .filter(Boolean)
}

/**
 * Prune menu items marked `bruceOwnerOnly` — shown only to Bruce's owner (the
 * `can_manage_access` flag from /assistant/config). Presentation only, like
 * superAdminOnly above: the endpoints behind such a page do their own check.
 */
export const pruneMenusByBruceOwner = (menus, isBruceOwner) =>
  menus
    .map((item) => {
      if (item.bruceOwnerOnly && !isBruceOwner) return null

      if (item.subMenus) {
        const subMenus = pruneMenusByBruceOwner(item.subMenus, isBruceOwner)
        const hasVisibleChild = subMenus.some(
          (sub) => sub.sideBarVisibility !== false
        )
        return hasVisibleChild ? { ...item, subMenus } : null
      }

      return item
    })
    .filter(Boolean)

/**
 * The first page this user is actually allowed to open.
 *
 * Alfred lands everyone on /home/parent, which a restricted role may not hold —
 * they would log in straight onto a "no access" screen. Send them to the first
 * thing their sidebar shows instead. Returns null if they can see nothing.
 */
export const firstAllowedPath = (access) => {
  let found = null

  const search = (menus) =>
    menus.forEach((item) => {
      if (found) return
      if (item.sideBarVisibility === false) return
      if (item.subMenus) return search(item.subMenus)
      if (item.path) found = item.path
    })

  search(pruneMenusByAccess(navigationMenus, access))
  return found
}

/** Check/uncheck a node together with its whole subtree, then fix ancestors. */
export const togglePermission = (selectedIds, id, checked) => {
  const next = new Set(selectedIds)
  const node = nodeById[id]
  const affected = [id, ...descendantIds(node)]
  affected.forEach((each) => (checked ? next.add(each) : next.delete(each)))
  return syncAncestors(next)
}

export const isIndeterminate = (node, selectedIds) =>
  !selectedIds.has(node.id) &&
  descendantIds(node).some((id) => selectedIds.has(id))
